import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { formatCurrency, formatNumber } from '../../../utils/formatters';
import { API_DOCS } from './StoricoPrezziListaPage.api';

// ── Tipi ──────────────────────────────────────────────────────────────────────
// I Decimal di Prisma arrivano serializzati come stringa.
type Dec = string | number | null;

interface RigaStorico {
  rigaId: number;
  societaId: number;
  societaNome: string | null;
  codiceArticolo: string;
  descrizioneBase: string;
  prezzoStandard: Dec;
  prezzoPrecedente: Dec;
  prezzoAttuale: Dec;
  umAcquisto: string | null;
  convertitore: Dec;
  stato: string;
  importId: number;
  etichetta: string;
  importatoAt: string;
  utenteName: string;
  strategiaAggiornamento: 'SOLO_PREZZO' | 'PREZZO_E_ANAGRAFICA';
  fornitoreId: number | null;
  fornitoreRagioneSociale: string | null;
  fornitoreNome: string | null;
  articoloId: number | null;
}

interface FornitoreOption {
  id: number;
  ragioneSociale: string | null;
  nome: string | null;
}

// ── Helper ────────────────────────────────────────────────────────────────────

function toNum(v: Dec | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function formatData(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function labelFornitore(f: { ragioneSociale: string | null; nome: string | null; id: number }): string {
  return f.ragioneSociale || f.nome || `#${f.id}`;
}

const LIMIT = 50;

// ── Pagina ────────────────────────────────────────────────────────────────────

const StoricoPrezziListaPage: React.FC = () => {
  const navigate = useNavigate();
  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const [righe, setRighe]   = useState<RigaStorico[]>([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const [fornitori, setFornitori] = useState<FornitoreOption[]>([]);

  // Filtri
  const [q, setQ]                         = useState('');
  const [fornitoreId, setFornitoreId]     = useState('');
  const [dal, setDal]                     = useState('');
  const [al, setAl]                       = useState('');
  // Default off: su archivi giovani ogni articolo ha un solo import applicato,
  // quindi nessuna variazione — la pagina si aprirebbe vuota.
  const [soloVariazioni, setSoloVariazioni] = useState(false);

  // Ricerca testuale con debounce, per non interrogare a ogni tasto
  const [qDebounced, setQDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Ogni cambio di filtro riparte da pagina 1
  useEffect(() => {
    setPage(1);
  }, [societaId, qDebounced, fornitoreId, dal, al, soloVariazioni]);

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (societaId) qs.set('societaId', societaId);
      if (qDebounced.trim()) qs.set('q', qDebounced.trim());
      if (fornitoreId) qs.set('fornitoreId', fornitoreId);
      if (dal) qs.set('dal', dal);
      if (al) qs.set('al', al);
      if (soloVariazioni) qs.set('soloVariazioni', 'true');
      qs.set('page', String(page));
      qs.set('limit', String(LIMIT));

      // @api GET /api/anagrafiche/articoli/storico-prezzi?societaId={id}&q={q}&fornitoreId={id}&dal={data}&al={data}&soloVariazioni={bool}&page={n}&limit={n} -> {"success":true,"data":[{"rigaId":"number","codiceArticolo":"string","descrizioneBase":"string","prezzoStandard":"string","prezzoPrecedente":"string|null","prezzoAttuale":"string|null","umAcquisto":"string|null","importId":"number","etichetta":"string","importatoAt":"string","utenteName":"string","fornitoreId":"number|null","fornitoreRagioneSociale":"string|null","articoloId":"number|null"}],"total":"number","page":"number","limit":"number"}
      const res = await apiFetch<{ success: boolean; data: RigaStorico[]; total: number }>(
        `/api/anagrafiche/articoli/storico-prezzi?${qs}`,
      );
      setRighe(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel caricamento dello storico prezzi');
      setRighe([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [societaId, qDebounced, fornitoreId, dal, al, soloVariazioni, page]);

  useEffect(() => {
    if (!pronta) return;
    carica();
  }, [pronta, carica]);

  // Combo fornitori: solo quelli che hanno almeno un import
  useEffect(() => {
    if (!pronta) return;
    const caricaFornitori = async () => {
      try {
        const qs = societaId ? `?societaId=${societaId}` : '';
        // @api GET /api/anagrafiche/articoli/storico-prezzi/fornitori?societaId={id} -> {"success":true,"data":[{"id":"number","ragioneSociale":"string|null","nome":"string|null"}]}
        const res = await apiFetch<{ success: boolean; data: FornitoreOption[] }>(
          `/api/anagrafiche/articoli/storico-prezzi/fornitori${qs}`,
        );
        setFornitori(res.data || []);
      } catch {
        setFornitori([]);
      }
    };
    caricaFornitori();
  }, [pronta, societaId]);

  const nPagine = Math.max(1, Math.ceil(total / LIMIT));
  const mostraSocieta = !societaId;

  const azzeraFiltri = () => {
    setQ('');
    setFornitoreId('');
    setDal('');
    setAl('');
    setSoloVariazioni(false);
  };

  return (
    <Layout>
      <div className="articolo-prezzi">
        <div className="bop-page-header">
          <h1 className="bop-page-title">Storico prezzi</h1>
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select
              className="bop-select"
              value={societaId}
              onChange={e => setSocietaId(e.target.value)}
            >
              <option value="">Tutte le società</option>
              {listaSocieta.map(s => (
                <option key={s.id} value={s.id}>{s.nome}</option>
              ))}
            </select>
          )}

          <input
            className="bop-input"
            placeholder="Codice o descrizione articolo…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />

          <select
            className="bop-select"
            value={fornitoreId}
            onChange={e => setFornitoreId(e.target.value)}
          >
            <option value="">Tutti i fornitori</option>
            {fornitori.map(f => (
              <option key={f.id} value={f.id}>{labelFornitore(f)}</option>
            ))}
          </select>

          <input
            type="date"
            className="bop-input"
            value={dal}
            onChange={e => setDal(e.target.value)}
            title="Import dal"
          />
          <input
            type="date"
            className="bop-input"
            value={al}
            onChange={e => setAl(e.target.value)}
            title="Import al"
          />

          <label className="articolo-prezzi__toggle">
            <input
              type="checkbox"
              checked={soloVariazioni}
              onChange={e => setSoloVariazioni(e.target.checked)}
            />
            Solo variazioni di prezzo
          </label>

          <button type="button" className="bop-btn-secondary" onClick={azzeraFiltri}>
            Azzera filtri
          </button>
        </div>

        <div className="bop-card">
          <div className="bop-card__header">
            <span className="bop-card__title">
              {loading
                ? 'Caricamento…'
                : total === 0
                  ? 'Nessuna riga'
                  : `${(page - 1) * LIMIT + 1}–${Math.min(page * LIMIT, total)} di ${total} righe`}
            </span>
            {nPagine > 1 && (
              <div className="articolo-prezzi__pager">
                <button
                  type="button"
                  className="bop-btn-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                >
                  ← Precedente
                </button>
                <span className="bop-text-muted">Pagina {page} di {nPagine}</span>
                <button
                  type="button"
                  className="bop-btn-secondary"
                  onClick={() => setPage(p => Math.min(nPagine, p + 1))}
                  disabled={page >= nPagine || loading}
                >
                  Successiva →
                </button>
              </div>
            )}
          </div>
          <div className="bop-card__body">
            {error && <p className="bop-text-danger">{error}</p>}

            {!error && righe.length === 0 && !loading ? (
              <div className="bop-empty-state">
                {soloVariazioni
                  ? 'Nessuna variazione di prezzo con questi filtri. Togli "Solo variazioni" per vedere anche gli import che hanno confermato il prezzo.'
                  : 'Nessuna riga di listino con questi filtri.'}
              </div>
            ) : (
              <div className="articolo-prezzi__table-wrap">
                <table className="bop-table bop-table--clickable">
                  <thead>
                    <tr>
                      <th>Data import</th>
                      {mostraSocieta && <th>Società</th>}
                      <th>Articolo</th>
                      <th>Fornitore</th>
                      <th>Etichetta import</th>
                      <th className="bop-text-right">Prezzo prec.</th>
                      <th className="bop-text-right">Prezzo import</th>
                      <th className="bop-text-right">Var. %</th>
                      <th className="bop-text-right">Prezzo oggi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {righe.map(r => {
                      const prezzo   = toNum(r.prezzoStandard);
                      const prec     = toNum(r.prezzoPrecedente);
                      const attuale  = toNum(r.prezzoAttuale);
                      const delta = prezzo != null && prec != null && prec !== 0
                        ? ((prezzo - prec) / prec) * 100
                        : null;
                      // Il prezzo in anagrafica non coincide con l'ultimo import:
                      // qualcuno l'ha modificato a mano dopo. Vale la pena vederlo.
                      const disallineato = attuale != null && prezzo != null && attuale !== prezzo;
                      return (
                        <tr
                          key={r.rigaId}
                          onClick={() => r.articoloId && navigate(`/anagrafiche/articoli/${r.articoloId}/storico-prezzi`)}
                        >
                          <td>{formatData(r.importatoAt)}</td>
                          {mostraSocieta && <td className="bop-text-muted">{r.societaNome || '—'}</td>}
                          <td>
                            <div className="articolo-prezzi__cod">{r.codiceArticolo}</div>
                            <div className="bop-text-muted articolo-prezzi__desc-cell">
                              {r.descrizioneBase}
                            </div>
                          </td>
                          <td>
                            {r.fornitoreRagioneSociale || r.fornitoreNome || '—'}
                          </td>
                          <td className="bop-text-muted">{r.etichetta}</td>
                          <td className="bop-text-right articolo-prezzi__num">
                            {prec == null ? <span className="bop-text-muted">—</span> : `€ ${formatCurrency(prec)}`}
                          </td>
                          <td className="bop-text-right articolo-prezzi__num">
                            € {formatCurrency(prezzo)}
                          </td>
                          <td className="bop-text-right articolo-prezzi__num">
                            {delta == null ? (
                              <span className="bop-text-muted">—</span>
                            ) : (
                              <span className={delta > 0
                                ? 'articolo-prezzi__delta--su'
                                : delta < 0 ? 'articolo-prezzi__delta--giu' : ''}
                              >
                                {delta > 0 ? '+' : ''}{formatNumber(delta)}%
                              </span>
                            )}
                          </td>
                          <td className="bop-text-right articolo-prezzi__num">
                            {attuale == null ? (
                              <span className="bop-text-muted">—</span>
                            ) : (
                              <>
                                € {formatCurrency(attuale)}
                                {disallineato && (
                                  <span
                                    className="articolo-prezzi__disallineato"
                                    title="Il prezzo in anagrafica non coincide con quello di questo import"
                                  >
                                    ≠
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="articolo-prezzi__nota bop-text-muted">
              Solo le righe che hanno effettivamente aggiornato l'anagrafica
              (stato Importato/Aggiornato). La variazione è calcolata sull'import
              applicato precedente per lo stesso codice articolo, indipendentemente
              dai filtri. Clicca una riga per lo storico completo dell'articolo.
            </p>
          </div>
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
};

export default StoricoPrezziListaPage;
